/**
 * ExploreCategoryRecipeFavorites - User's favorite recipes section
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { Link } from 'react-router-dom';
import { ExploreCategory } from '../../Model/Explore/ExploreCategory';
import { Recipe, RecipeApi } from '../../Model/Diet/Recipe/Recipe';
import { useRootStore } from '../../Store/useRootStore';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { Theme } from '../../Theme/Theme';
import { TileCard } from './TileCard';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Section = styled.div`
  padding: 32px 0;
  background: white;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
`;

const TitleGroup = styled.div``;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.25rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.9rem;
  color: ${Theme.Colors.charcoal};
  margin: 4px 0 0 0;
`;

const ShowAllLink = styled(Link)`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${Theme.Colors.primary};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const HorizontalScroll = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 8px 24px 16px 24px;
  margin: 0 -24px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CardWrapper = styled.div`
  flex: 0 0 280px;
  min-width: 280px;

  @media (max-width: 600px) {
    flex: 0 0 220px;
    min-width: 220px;
  }
`;

const SkeletonCard = styled.div`
  flex: 0 0 280px;
  min-width: 280px;

  @media (max-width: 600px) {
    flex: 0 0 220px;
    min-width: 220px;
  }
`;

const SkeletonImage = styled.div`
  aspect-ratio: 1;
  border-radius: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonText = styled.div<{ width?: string }>`
  height: 14px;
  width: ${({ width }) => width || '100%'};
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  margin-top: 12px;
`;

const EmptyCard = styled.div`
  background: ${Theme.Colors.lightestGrey};
  border-radius: 16px;
  padding: 32px 24px;
  text-align: center;
  border: 2px dashed ${Theme.Colors.lighterGrey};
  flex: 0 0 100%;
  max-width: 400px;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const EmptyTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const EmptySubtitle = styled.p`
  font-size: 0.85rem;
  color: ${Theme.Colors.warmGrey};
  margin: 0;
`;

export type ExploreCategoryRecipeFavoritesProps = {
  category: ExploreCategory;
  linkPrefix?: string;
};

export const ExploreCategoryRecipeFavorites: React.FC<ExploreCategoryRecipeFavoritesProps> = observer(
  ({ category, linkPrefix = '/nutrition/' }) => {
    const { authentication } = useRootStore();
    const { t } = useTrans();

    const [favorites, setFavorites] = useState<Recipe[]>([]);
    const [loading, setLoading] = useState(true);

    const title = useLocalized(category.name, t('nutrition.favorites.title', { defaultValue: 'Your Favorites' }));
    const description = useLocalized(
      category.description,
      t('nutrition.favorites.subtitle', { defaultValue: "Recipes you've saved" }),
    );

    useEffect(() => {
      if (authentication.athlete) {
        setLoading(true);
        RecipeApi.findFavorites({ size: 10 })
          .then((result) => setFavorites(result))
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }, [authentication.athlete]);

    const renderSkeletonCards = () =>
      Array.from({ length: 4 }).map((_, i) => (
        <SkeletonCard key={`skeleton-${i}`}>
          <SkeletonImage />
          <SkeletonText width="80%" />
          <SkeletonText width="50%" />
        </SkeletonCard>
      ));

    if (!authentication.athlete) {
      return null;
    }

    return (
      <Section>
        <Container>
          <SectionHeader>
            <TitleGroup>
              <Title>{title}</Title>
              <Subtitle>{description}</Subtitle>
            </TitleGroup>
            {favorites.length > 0 && (
              <ShowAllLink to={`${linkPrefix}favorites`}>
                {t('globals.showAll', { defaultValue: 'Show All' })}
              </ShowAllLink>
            )}
          </SectionHeader>
          <HorizontalScroll>
            {loading ? (
              renderSkeletonCards()
            ) : favorites.length === 0 ? (
              <EmptyCard>
                <EmptyIcon>🍽️</EmptyIcon>
                <EmptyTitle>{t('nutrition.favorites.empty', { defaultValue: 'No favorites yet' })}</EmptyTitle>
                <EmptySubtitle>
                  {t('nutrition.favorites.emptyHint', { defaultValue: 'Save recipes you love to find them here' })}
                </EmptySubtitle>
              </EmptyCard>
            ) : (
              favorites.map((recipe) => (
                <CardWrapper key={recipe.id}>
                  <TileCard entry={recipe} linkPrefix={linkPrefix} />
                </CardWrapper>
              ))
            )}
          </HorizontalScroll>
        </Container>
      </Section>
    );
  },
);
